import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "aws-amplify/auth";
import { FiAlertTriangle } from "react-icons/fi";
import { fetchDatasets } from "../../store/datasetSlice";
import { observedColumns } from "../../utils/datasetColumnMapping";
import { datasetPickVerdict } from "../../utils/historicalModelRuns";
import { isCuratedDataset } from "../../utils/datasetQueryApi";
import type { AppDispatch, RootState } from "../../store";
import type { FieldDefinition } from "../../types/modelSchemas";

export interface DatasetFieldValue {
  type?: "dataset";
  datasetId?: string | null;
  columns?: string[];
}

export interface DatasetFieldContext {
  /** The model's owner as the wizard has it: the dataset must belong to the same org (or the user), or the run
   * function refuses the link; an Admin owner is let through with a warning (stage 3, decision 5) */
  ownerType?: "personal" | "organization";
  selectedOrgKey?: string;
  isAdmin?: boolean;
  /** The model being edited, so a model_output field never offers the model its own outputs */
  editingModelId?: string;
  /** The template's kind: a HISTORICAL template's outputs always go to a dataset (OutputDestinationField) */
  modelKind?: string | null;
  /** The template's historicalConfig.mergePolicy: which duplicate policy its outputs' datasets need */
  mergePolicy?: string | null;
}

interface DatasetFieldProps {
  fieldKey: string;
  field: Pick<FieldDefinition, "label" | "description" | "validation"> | null | undefined;
  value: DatasetFieldValue | null | undefined;
  onChange: (value: DatasetFieldValue | null) => void;
  error?: string;
  context?: DatasetFieldContext;
}

/**
 * The `dataset` field type (stage 3 of docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md): a historical dataset the user
 * can read, and the columns the model processes. Datasets without curation are listed but can't be picked (stage 4,
 * decision 11): the model could never read them, and the fix is an admin's.
 */
const DatasetField: React.FC<DatasetFieldProps> = ({ fieldKey, field, value, onChange, error, context }) => {
  const dispatch = useDispatch<AppDispatch>();
  const personal = useSelector((state: RootState) => state.datasets?.personalDatasets || {});
  const organization = useSelector((state: RootState) => state.datasets?.organizationDatasets || {});
  const loading = useSelector((state: RootState) => state.datasets?.loading);
  const organizationsById = useSelector((state: RootState) => state.user?.organizations?.data || {});
  const organizationIdsKey = Object.keys(organizationsById).sort().join(",");

  const datasets = useMemo(() => {
    const all = [...Object.values(personal), ...Object.values(organization)] as any[];
    // A historical model reads historical datasets; forecast datasets are a different thing
    return all
      .filter((d) => d && d.datasetType !== "FORECAST" && d.datasetType !== "ENSEMBLE_FORECAST")
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [personal, organization]);

  // The templates page doesn't load datasets; read them once here when the store has none
  useEffect(() => {
    if (datasets.length || loading === "pending") return;
    let cancelled = false;
    getCurrentUser()
      .then((user) => {
        if (cancelled) return;
        dispatch(fetchDatasets({ userId: user.userId, organizationIds: organizationIdsKey ? organizationIdsKey.split(",") : [] }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationIdsKey]);

  const config = field?.validation?.datasetConfig || {};
  const multi = config.columns !== "single";
  const requireCurated = config.requireCurated !== false;
  const picked = datasets.find((d) => d.id === value?.datasetId) || null;
  const columns = picked ? observedColumns(picked) : [];
  const ticked = Array.isArray(value?.columns) ? (value?.columns as string[]) : [];

  const pickDataset = (datasetId: string) => {
    if (!datasetId) {
      onChange(null);
      return;
    }
    // Keep the ticked columns the new dataset also has, so swapping between like datasets keeps their rules
    const next = datasets.find((d) => d.id === datasetId);
    const available = next ? observedColumns(next) : [];
    const kept = ticked.filter((c) => available.includes(c));
    onChange({ type: "dataset", datasetId, columns: multi ? kept : kept.slice(0, 1) });
  };

  const toggleColumn = (column: string) => {
    if (!value?.datasetId) return;
    const next = multi ? (ticked.includes(column) ? ticked.filter((c) => c !== column) : [...ticked, column]) : [column];
    onChange({ type: "dataset", datasetId: value.datasetId, columns: next });
  };

  // The run function's own rule (stage 3, decision 5): a dataset outside the model's organization (or, for a
  // personal model, any organization's) is refused unless the owner is an Admin, who gets the amber note instead
  const verdictFor = (dataset: { organizationId?: string | null } | null | undefined) =>
    datasetPickVerdict({ ...(context ?? {}), datasetOrganizationId: dataset?.organizationId });
  const verdict = picked ? verdictFor(picked) : null;

  return (
    <div className="space-y-3" data-testid={`dataset-field-${fieldKey}`}>
      <select value={value?.datasetId || ""} onChange={(e) => pickDataset(e.target.value)} className={`eai-select w-full ${error ? "border-eai-danger" : ""}`} aria-label={field?.label || "Dataset"} data-testid="dataset-select">
        <option value="">{loading === "pending" && !datasets.length ? "Loading datasets…" : "Choose a dataset"}</option>
        {datasets.map((d) => {
          const curated = isCuratedDataset(d);
          const refused = verdictFor(d).refused;
          const disabled = (requireCurated && !curated) || Boolean(refused);
          return (
            <option key={d.id} value={d.id} disabled={disabled}>
              {d.name}
              {requireCurated && !curated ? " · curation is off; ask an Erode admin to turn it on" : refused ? ` · ${refused}` : ""}
            </option>
          );
        })}
      </select>
      {loading !== "pending" && datasets.length === 0 && <p className="text-xs text-gray-500">No historical datasets you can read yet.</p>}

      {picked && (
        <div className="space-y-1" data-testid="dataset-columns">
          <span className="block text-xs font-medium text-gray-700">{multi ? "Columns to process" : "Column to process"}</span>
          {columns.length === 0 ? (
            <p className="text-xs text-gray-500">This dataset has no columns yet: send it data first.</p>
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {columns.map((column) => (
                <label key={column} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type={multi ? "checkbox" : "radio"} name={`${fieldKey}-columns`} checked={ticked.includes(column)} onChange={() => toggleColumn(column)} data-testid={`column-${column}`} />
                  {column}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {verdict?.refused && (
        <p className="flex items-center gap-1 text-xs text-eai-danger-text" data-testid="dataset-refused">
          <FiAlertTriangle size={12} aria-hidden />
          This model can't read that dataset: {verdict.refused}. Pick another, or change the owner above.
        </p>
      )}
      {verdict?.crossOrg && !verdict.refused && (
        <p className="flex items-center gap-1 text-xs text-eai-warning-text" data-testid="dataset-cross-org">
          <FiAlertTriangle size={12} aria-hidden />
          This model doesn't own that dataset. Only an Admin owner can link it, and the model page will say so.
        </p>
      )}
    </div>
  );
};

export default DatasetField;
